import React from 'react';

import { Button as RadixButton } from '@radix-ui/themes';

import type { ButtonProps as RadixButtonProps } from '@radix-ui/themes';

type ButtonVariant = RadixButtonProps['variant'] | 'white-bg';

export interface ButtonProps extends Omit<RadixButtonProps, 'variant'> {
	variant?: ButtonVariant;
}

/**
 * Trigger an action or event, such as submitting a form or displaying a dialog.
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	(props, ref) => {
		const { variant, style, ...rest } = props;

		if (variant === 'white-bg') {
			// Aligned with Figma button system "default" variant (node 5889:40331).
			// Falls back to legacy --color-text-primary / --gray-6 if the new
			// --text-primary / --border-default tokens aren't defined.
			const whiteBackgroundStyle = {
				backgroundColor: 'var(--surface-primary, white)',
				color: 'var(--text-primary, var(--color-text-primary))',
				border: '1px solid var(--border-default, var(--gray-6))',
				cursor: 'pointer',
				...style,
			};

			return <RadixButton {...rest} style={whiteBackgroundStyle} ref={ref} />;
		}

		// Only pass the variant if it's a valid RadixButton variant
		const radixVariant = variant as Exclude<ButtonVariant, 'white-bg'>;
		return (
			<RadixButton
				{...rest}
				style={{ cursor: 'pointer', ...style }}
				variant={radixVariant}
				ref={ref}
			/>
		);
	},
);

export default Button;
